/**
 * useSmartPosition — 统一智能 Tooltip 定位引擎
 *
 * 解决问题：代码库中 8 种 tooltip 机制各自硬编码定位，只有 1/7 做了视口碰撞检测。
 * 本 composable 提供统一的 computeSmartPosition() 纯函数，实现：
 *
 * 1. Flip：首选方向空间不足时自动翻转到对侧（上→下，左→右）
 * 2. Shift：在选定方向上水平/垂直 clamp，防止溢出视口
 * 3. 视口安全边距：确保 tooltip 不贴边/不遮挡滚动条
 *
 * 算法参考 Floating-UI 的 flip + shift 策略，但零依赖、纯数学计算。
 * 所有返回坐标为 fixed 定坐标系（position: fixed），脱离父容器 overflow 限制。
 */

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

export interface SmartPositionOptions {
  /** 锚点模式：'element' = 相对触发元素的 bounding rect；'cursor' = 相对光标位置 */
  anchor: 'element' | 'cursor';
  /** 期望的优先方向（空间不足时自动 flip） */
  preferredSide?: TooltipSide;
  /** tooltip 预估尺寸（用于碰撞计算） */
  tooltipSize: { width: number; height: number };
  /** 触发元素与 tooltip 之间的间距，默认 12px */
  margin?: number;
  /** 视口安全边距（离屏幕边缘的最小距离），默认 8px */
  viewportPadding?: number;
}

export interface SmartPositionResult {
  /** fixed 定位 X 坐标（left） */
  x: number;
  /** fixed 定位 Y 坐标（top） */
  y: number;
  /** 实际使用的方向（可能因 flip 与 preferredSide 不同） */
  side: TooltipSide;
  /** 是否发生了翻转 */
  flipped: boolean;
}

/** 视口尺寸（带 SSR 安全 fallback） */
function getViewport(): { w: number; h: number } {
  if (typeof window === 'undefined') return { w: 1920, h: 1080 };
  return { w: window.innerWidth, h: window.innerHeight };
}

/**
 * 计算智能定位
 *
 * @param triggerRect 触发元素的 getBoundingClientRect()；anchor='cursor' 时传 null
 * @param cursorPos   光标位置 {x, y}；anchor='element' 时传 null
 * @param options     定位选项
 * @returns SmartPositionResult 含 fixed 坐标和实际方向
 */
export function computeSmartPosition(
  triggerRect: DOMRect | null,
  cursorPos: { x: number; y: number } | null,
  options: SmartPositionOptions
): SmartPositionResult {
  const {
    anchor,
    preferredSide = 'top',
    tooltipSize,
    margin = 12,
    viewportPadding = 8,
  } = options;

  const vp = getViewport();
  const tw = tooltipSize.width;
  const th = tooltipSize.height;

  // ── 解析锚点坐标 ──
  // 对于 element 模式，锚点是触发元素的边界矩形
  // 对于 cursor 模式，锚点是光标点（视为 1x1 矩形）
  let anchorLeft: number, anchorTop: number, anchorRight: number, anchorBottom: number;

  if (anchor === 'cursor' && cursorPos) {
    anchorLeft = cursorPos.x;
    anchorTop = cursorPos.y;
    anchorRight = cursorPos.x;
    anchorBottom = cursorPos.y;
  } else if (triggerRect) {
    anchorLeft = triggerRect.left;
    anchorTop = triggerRect.top;
    anchorRight = triggerRect.right;
    anchorBottom = triggerRect.bottom;
  } else {
    // Fallback: 居中
    return { x: (vp.w - tw) / 2, y: (vp.h - th) / 2, side: preferredSide, flipped: false };
  }

  const anchorWidth = anchorRight - anchorLeft;
  const anchorHeight = anchorBottom - anchorTop;
  const anchorCenterX = anchorLeft + anchorWidth / 2;
  const anchorCenterY = anchorTop + anchorHeight / 2;

  // ── 计算每个方向的可用空间 ──
  const space = {
    top: anchorTop - viewportPadding,
    bottom: vp.h - anchorBottom - viewportPadding,
    left: anchorLeft - viewportPadding,
    right: vp.w - anchorRight - viewportPadding,
  };

  // ── Step 1: Flip — 判断首选方向是否有足够空间 ──
  const oppositeMap: Record<TooltipSide, TooltipSide> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  };

  let side = preferredSide;
  let flipped = false;

  const needsSpace = side === 'top' || side === 'bottom' ? th + margin : tw + margin;

  if (space[side] < needsSpace && space[oppositeMap[side]] >= needsSpace) {
    side = oppositeMap[side];
    flipped = true;
  }

  // ── Step 2: Shift — 在选定方向上 clamp 防溢出 ──
  let x: number;
  let y: number;

  const minX = viewportPadding;
  const maxX = vp.w - tw - viewportPadding;
  const minY = viewportPadding;
  const maxY = vp.h - th - viewportPadding;

  switch (side) {
    case 'top':
      // tooltip 在锚点上方，水平居中
      y = anchorTop - th - margin;
      x = anchorCenterX - tw / 2;
      break;
    case 'bottom':
      // tooltip 在锚点下方，水平居中
      y = anchorBottom + margin;
      x = anchorCenterX - tw / 2;
      break;
    case 'left':
      // tooltip 在锚点左侧，垂直居中
      x = anchorLeft - tw - margin;
      y = anchorCenterY - th / 2;
      break;
    case 'right':
      // tooltip 在锚点右侧，垂直居中
      x = anchorRight + margin;
      y = anchorCenterY - th / 2;
      break;
  }

  // ── Step 3: Clamp 到视口安全区域 ──
  // 水平 clamp（对所有方向生效）
  if (x < minX) x = minX;
  if (x > maxX) x = maxX;

  // 垂直 clamp（对所有方向生效）
  if (y < minY) y = minY;
  if (y > maxY) y = maxY;

  // 特殊处理：cursor 模式下，如果 tooltip 覆盖了光标位置，额外偏移
  // 这避免 tooltip 遮挡用户正在看的元素
  if (anchor === 'cursor' && cursorPos) {
    // 如果 tooltip 区域覆盖了光标点，根据方向推离
    const coversCursorX = x <= cursorPos.x && x + tw >= cursorPos.x;
    const coversCursorY = y <= cursorPos.y && y + th >= cursorPos.y;
    if (coversCursorX && coversCursorY) {
      // 推到光标的另一侧
      if (side === 'top' || side === 'bottom') {
        // 垂直方向已定，尝试水平避开
        if (cursorPos.x > vp.w / 2) {
          x = Math.max(minX, cursorPos.x - tw - margin);
        } else {
          x = Math.min(maxX, cursorPos.x + margin);
        }
      } else {
        // 水平方向已定，尝试垂直避开
        if (cursorPos.y > vp.h / 2) {
          y = Math.max(minY, cursorPos.y - th - margin);
        } else {
          y = Math.min(maxY, cursorPos.y + margin);
        }
      }
    }
  }

  return { x: Math.round(x), y: Math.round(y), side, flipped };
}

/**
 * 将 SmartPositionResult 转为 CSS style 对象
 * 用于 :style 绑定
 */
export function smartPositionToStyle(pos: SmartPositionResult): Record<string, string> {
  return {
    position: 'fixed',
    left: `${pos.x}px`,
    top: `${pos.y}px`,
  };
}
